/**
 * Group Reward Templates Page
 * Displays and manages reward templates for a group
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material'
import {
  Add,
  CardGiftcard,
  CheckCircle,
  Cancel,
  ArrowBack,
  MoreVert,
  Edit,
  BarChart,
  Send,
  Pause,
  PlayArrow,
  Delete,
} from '@mui/icons-material'
import { useRewardTemplates, useToggleTemplateActive, useDeleteTemplate } from '../hooks/useRewardTemplates'
import { DistributeRewardsDialog } from '../components/DistributeRewardsDialog'
import { getContentTypeName, getDistributionTypeName } from '@api/types/rewards'
import { getContentTypeColor } from '../utils/rewardHelpers'
import type { GiftRewardTemplateDto } from '@api/types/rewards'

type FilterTab = 'all' | 'active' | 'inactive'

export default function GroupRewardTemplatesPage() {
  const { id: groupId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [filterTab, setFilterTab] = useState<FilterTab>('all')
  const [page, setPage] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<GiftRewardTemplateDto | null>(null)
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false)
  const [templateToDistribute, setTemplateToDistribute] = useState<GiftRewardTemplateDto | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<GiftRewardTemplateDto | null>(null)

  const { data, isLoading, error } = useRewardTemplates(groupId, page + 1)
  const toggleActiveMutation = useToggleTemplateActive()
  const deleteMutation = useDeleteTemplate()

  const templates = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const pageSize = data?.pageSize ?? 10

  // Filter templates based on active tab (client-side for now)
  const filteredTemplates = templates.filter((t) => {
    if (filterTab === 'active') return t.isActive
    if (filterTab === 'inactive') return !t.isActive
    return true
  })

  const handleEdit = (template: GiftRewardTemplateDto) => {
    navigate(`/groups/${groupId}/rewards/${template.id}/edit`)
  }

  const handleViewStats = (template: GiftRewardTemplateDto) => {
    navigate(`/groups/${groupId}/rewards/${template.id}/stats`)
  }

  const handleDistribute = (template: GiftRewardTemplateDto) => {
    setTemplateToDistribute(template)
    setDistributeDialogOpen(true)
  }

  const handleToggleActive = async (template: GiftRewardTemplateDto) => {
    if (!groupId) return
    await toggleActiveMutation.mutateAsync({ groupId, templateId: template.id })
  }

  const handleDeleteClick = (template: GiftRewardTemplateDto) => {
    setTemplateToDelete(template)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!templateToDelete || !groupId) return
    await deleteMutation.mutateAsync({ groupId, templateId: templateToDelete.id })
    setDeleteDialogOpen(false)
    setTemplateToDelete(null)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: GiftRewardTemplateDto) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedTemplate(template)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setSelectedTemplate(null)
  }

  const handleMenuAction = (action: () => void) => {
    handleMenuClose()
    action()
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/groups/${groupId}`)}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Reward Templates
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate(`/groups/${groupId}/rewards/new`)}
        >
          Create Template
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load reward templates. Please try again.
        </Alert>
      )}

      {/* Filter tabs */}
      <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={filterTab}
          onChange={(_, v) => {
            setFilterTab(v)
            setPage(0)
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label="All" icon={<CardGiftcard />} iconPosition="start" />
          <Tab value="active" label="Active" icon={<CheckCircle />} iconPosition="start" />
          <Tab value="inactive" label="Inactive" icon={<Cancel />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Empty state */}
      {!isLoading && templates.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardGiftcard sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Reward Templates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first reward template to start distributing rewards to users.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/groups/${groupId}/rewards/new`)}
          >
            Create Template
          </Button>
        </Paper>
      )}

      {/* Templates table */}
      {templates.length > 0 && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Template</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Distribution</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Distributed</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Active</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No {filterTab === 'active' ? 'active' : 'inactive'} templates found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTemplates.map((template) => (
                    <TableRow
                      key={template.id}
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        opacity: template.isActive ? 1 : 0.7,
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CardGiftcard color={template.isActive ? 'primary' : 'disabled'} />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {template.title || 'Untitled Template'}
                            </Typography>
                            {template.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  maxWidth: 250,
                                }}
                              >
                                {template.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getContentTypeName(template.contentType)}
                          size="small"
                          color={getContentTypeColor(template.contentType)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getDistributionTypeName(template.distributionType)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={template.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={template.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {template.totalDistributed ?? 0}
                          {template.totalLimit && ` / ${template.totalLimit}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {template.activeInstances ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Stats">
                            <IconButton size="small" onClick={() => handleViewStats(template)}>
                              <BarChart fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {template.isActive && (
                            <Tooltip title="Distribute">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleDistribute(template)}
                              >
                                <Send fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, template)}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={filterTab === 'all' ? totalCount : filteredTemplates.length}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={() => {}}
          />
        </Paper>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => selectedTemplate && handleMenuAction(() => handleEdit(selectedTemplate))}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedTemplate && handleMenuAction(() => handleToggleActive(selectedTemplate))}>
          <ListItemIcon>
            {selectedTemplate?.isActive ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{selectedTemplate?.isActive ? 'Deactivate' : 'Activate'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => selectedTemplate && handleMenuAction(() => handleDeleteClick(selectedTemplate))}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{templateToDelete?.title}"? This action cannot be undone.
            Any distributed instances will remain with their owners.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Distribute dialog */}
      <DistributeRewardsDialog
        open={distributeDialogOpen}
        onClose={() => {
          setDistributeDialogOpen(false)
          setTemplateToDistribute(null)
        }}
        template={templateToDistribute}
      />
    </Box>
  )
}
